import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAnswer, useLanguage, usePracticeQueue } from '../api/hooks'
import type { AnswerResult, PracticeCard } from '../api/types'
import { Badge, Button, Card, EmptyState, ErrorText, Input, Spinner } from '../components/ui'

type Phase = { kind: 'asking' } | { kind: 'answered'; result: AnswerResult; given: string }

interface Tally {
  correct: number
  wrong: number
}

export default function Practice() {
  const languageId = Number(useParams().id)
  const { data: language } = useLanguage(languageId)
  const { data: queue, isPending, error, refetch } = usePracticeQueue(languageId, 'both')
  const answer = useAnswer()

  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>({ kind: 'asking' })
  const [typed, setTyped] = useState('')
  const [tally, setTally] = useState<Tally>({ correct: 0, wrong: 0 })

  const inputRef = useRef<HTMLInputElement>(null)
  const continueRef = useRef<HTMLButtonElement>(null)

  const card: PracticeCard | undefined = queue?.[index]

  // Move focus to whatever the learner should be interacting with, so the
  // whole session runs on the keyboard without ever tabbing around.
  useEffect(() => {
    if (phase.kind === 'asking') inputRef.current?.focus()
    else continueRef.current?.focus()
  }, [phase, index])

  if (isPending) return <Spinner label="Building your queue" />
  if (error) return <ErrorText error={error} />

  if (!queue || queue.length === 0) {
    return (
      <EmptyState
        title="Nothing due right now"
        hint="Every card in this language is scheduled for later. Add more words, or come back when the next review falls due."
        action={
          <Link to={`/languages/${languageId}`}>
            <Button>Back to {language?.name ?? 'the language'}</Button>
          </Link>
        }
      />
    )
  }

  // --- end of session ------------------------------------------------------
  if (!card) {
    const total = tally.correct + tally.wrong
    return (
      <div className="mx-auto max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Session complete</h1>
        <Card>
          <p className="text-4xl font-semibold tabular-nums">
            {tally.correct}
            <span className="text-muted">/{total}</span>
          </p>
          <p className="mt-1 text-sm text-muted">
            {tally.wrong === 0
              ? 'A clean sweep.'
              : `${tally.wrong} to see again sooner.`}
          </p>
        </Card>
        <div className="flex justify-center gap-2">
          <Button
            onClick={() => {
              setIndex(0)
              setTally({ correct: 0, wrong: 0 })
              setPhase({ kind: 'asking' })
              refetch()
            }}
          >
            Practice more
          </Button>
          <Link to={`/languages/${languageId}`}>
            <Button variant="secondary">Done</Button>
          </Link>
        </div>
      </div>
    )
  }

  // --- the card ------------------------------------------------------------
  function submit(event: FormEvent) {
    event.preventDefault()
    if (!card || phase.kind !== 'asking') return

    answer.mutate(
      { entry_id: card.entry_id, direction: card.direction, given: typed },
      {
        onSuccess: (result) => {
          setPhase({ kind: 'answered', result, given: typed })
          setTally((t) => ({
            correct: t.correct + (result.correct ? 1 : 0),
            wrong: t.wrong + (result.correct ? 0 : 1),
          }))
        },
      },
    )
  }

  function next() {
    setIndex((i) => i + 1)
    setTyped('')
    setPhase({ kind: 'asking' })
  }

  /** "I was actually right" -- re-grades the card as a clean success. */
  function overrideAsCorrect() {
    if (!card || phase.kind !== 'answered') return
    answer.mutate(
      { entry_id: card.entry_id, direction: card.direction, grade: 5 },
      {
        onSuccess: () => {
          setTally((t) => ({ correct: t.correct + 1, wrong: Math.max(0, t.wrong - 1) }))
          next()
        },
      },
    )
  }

  const progress = ((index + (phase.kind === 'answered' ? 1 : 0)) / queue.length) * 100

  return (
    <div className="mx-auto max-w-lg space-y-5">
      {/* progress */}
      <div>
        <div className="mb-2 flex items-center justify-between text-sm text-muted">
          <Link to={`/languages/${languageId}`} className="hover:text-ink">
            ← {language?.name}
          </Link>
          <span className="tabular-nums">
            {index + 1} / {queue.length}
          </span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-border-subtle">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <Card className="space-y-5">
        <div className="flex items-center gap-2">
          <Badge tone={card.direction === 'produce' ? 'brand' : 'neutral'}>
            {card.direction === 'produce'
              ? `${language?.source_language ?? 'English'} → ${language?.name ?? 'your language'}`
              : `${language?.name ?? 'your language'} → ${language?.source_language ?? 'English'}`}
          </Badge>
          {card.is_new && <Badge tone="positive">New</Badge>}
        </div>

        <p className="text-center text-3xl font-semibold tracking-tight">{card.prompt}</p>

        {phase.kind === 'asking' ? (
          <form onSubmit={submit} className="space-y-3">
            <Input
              ref={inputRef}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type your answer"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="text-center text-lg"
            />
            <Button type="submit" className="w-full" disabled={answer.isPending}>
              Check
            </Button>
            <p className="text-center text-xs text-muted">
              Press Enter to check. Leave it blank if you have no idea.
            </p>
          </form>
        ) : (
          <Feedback
            result={phase.result}
            given={phase.given}
            onNext={next}
            onOverride={overrideAsCorrect}
            continueRef={continueRef}
          />
        )}

        <ErrorText error={answer.error} />
      </Card>
    </div>
  )
}

function Feedback({
  result,
  given,
  onNext,
  onOverride,
  continueRef,
}: {
  result: AnswerResult
  given: string
  onNext: () => void
  onOverride: () => void
  continueRef: React.RefObject<HTMLButtonElement | null>
}) {
  // A near miss is marked correct but graded down; say so, otherwise the
  // learner cannot tell why a "correct" answer came back sooner than expected.
  const nearMiss = result.correct && result.grade < 5

  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg px-4 py-3 text-center ${
          result.correct ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
        }`}
      >
        <p className="font-medium">
          {result.correct ? (nearMiss ? 'Close enough' : 'Correct') : 'Not quite'}
        </p>
        {!result.correct && given.trim() !== '' && (
          <p className="mt-1 text-sm line-through opacity-70">{given}</p>
        )}
        {(!result.correct || nearMiss) && (
          <p className="mt-1 text-lg font-semibold text-ink">{result.expected}</p>
        )}
      </div>

      <p className="text-center text-xs text-muted">
        Next review in {result.interval_days}{' '}
        {result.interval_days === 1 ? 'day' : 'days'}
      </p>

      <Button ref={continueRef} onClick={onNext} className="w-full">
        Continue
      </Button>

      {!result.correct && (
        <button
          onClick={onOverride}
          className="w-full text-center text-xs text-muted hover:text-ink hover:underline"
        >
          I was actually right — count it
        </button>
      )}
    </div>
  )
}
